/**
 * TeamJudgmentPattern Management API Client
 */
import { createAuthenticatedClient } from '../../../shared/api/axiosClient';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let apiClient: ReturnType<typeof createAuthenticatedClient> | null = null;

const getApiClient = () => {
  if (!apiClient) {
    apiClient = createAuthenticatedClient(API_BASE_URL, () => {
      window.location.href = '/login';
    });
  }
  return apiClient;
};

// scoreConditions can be either:
// - Simple string values: { "TP": "RC" }
// - Range objects: { "A": { "min": 10, "max": 20 } }
export type ScoreConditionValue = string | { min: number; max: number };
export type ScoreConditions = Record<string, ScoreConditionValue>;

export interface TeamJudgmentPattern {
  id: string;
  name: string;
  description: string;
  scoreConditions: ScoreConditions;
  tdesc11?: string | null;
  tdesc12?: string | null;
  tdesc13?: string | null;
  tdesc14?: string | null;
  tdesc15?: string | null;
  tdesc16?: string | null;
  tdesc17?: string | null;
  tdesc18?: string | null;
  tdesc19?: string | null;
  tdesc21?: string | null;
  tdesc22?: string | null;
  tdesc23?: string | null;
  tdesc24?: string | null;
  tdesc25?: string | null;
  tdesc26?: string | null;
  tdesc27?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GetPatternsParams {
  name?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  limit?: number;
  offset?: number;
}

export interface GetPatternsResponse {
  patterns: TeamJudgmentPattern[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface CreatePatternInput {
  name: string;
  description: string;
  scoreConditions: ScoreConditions;
  tdesc11?: string;
  tdesc12?: string;
  tdesc13?: string;
  tdesc14?: string;
  tdesc15?: string;
  tdesc16?: string;
  tdesc17?: string;
  tdesc18?: string;
  tdesc19?: string;
  tdesc21?: string;
  tdesc22?: string;
  tdesc23?: string;
  tdesc24?: string;
  tdesc25?: string;
  tdesc26?: string;
  tdesc27?: string;
}

export interface UpdatePatternInput extends Partial<CreatePatternInput> {}

export type BulkImportMode = 'add' | 'replace';

export interface BulkImportResult {
  success: boolean;
  totalCount: number;
  successCount: number;
  errorCount: number;
  errors: Array<{
    index: number;
    name: string;
    error: string;
  }>;
  deletedCount?: number;
}

export async function getPatterns(params: GetPatternsParams): Promise<GetPatternsResponse> {
  const client = getApiClient();
  const response = await client.get<GetPatternsResponse>('/admin/team-judgment-patterns', { params });
  return response.data;
}

export async function getPatternById(id: string): Promise<TeamJudgmentPattern> {
  const client = getApiClient();
  const response = await client.get<{ pattern: TeamJudgmentPattern }>(`/admin/team-judgment-patterns/${id}`);
  return response.data.pattern;
}

export async function createPattern(input: CreatePatternInput): Promise<TeamJudgmentPattern> {
  const client = getApiClient();
  const response = await client.post<{ pattern: TeamJudgmentPattern }>('/admin/team-judgment-patterns', input);
  return response.data.pattern;
}

export async function updatePattern(id: string, input: UpdatePatternInput): Promise<TeamJudgmentPattern> {
  const client = getApiClient();
  const response = await client.put<{ pattern: TeamJudgmentPattern }>(`/admin/team-judgment-patterns/${id}`, input);
  return response.data.pattern;
}

export async function deletePattern(id: string): Promise<void> {
  const client = getApiClient();
  await client.delete(`/admin/team-judgment-patterns/${id}`);
}

export async function exportPattern(id: string): Promise<string> {
  const client = getApiClient();
  const response = await client.get<{ jsonData: string }>(`/admin/team-judgment-patterns/${id}/export`);
  return response.data.jsonData;
}

export async function importPattern(jsonData: string): Promise<TeamJudgmentPattern> {
  const client = getApiClient();
  const response = await client.post<{ pattern: TeamJudgmentPattern }>('/admin/team-judgment-patterns/import', { jsonData });
  return response.data.pattern;
}

export async function bulkImportPatterns(patterns: CreatePatternInput[], mode: BulkImportMode): Promise<BulkImportResult> {
  const client = getApiClient();
  const response = await client.post<{ result: BulkImportResult }>('/admin/team-judgment-patterns/bulk-import', { patterns, mode });
  return response.data.result;
}

/**
 * Find team judgment pattern by name/code (RC/RD/LA/LB/BB/BC/TA/TD/CC/EE).
 * 組織レポートの集約A/B/C/D判定 (classifyOrgType) で得たコードでパターンを引く。
 * Returns the pattern whose `name` matches the code, or null if not found / on error.
 */
export async function findPatternByCode(code: string): Promise<TeamJudgmentPattern | null> {
  try {
    const client = getApiClient();
    const response = await client.get<GetPatternsResponse>('/admin/team-judgment-patterns', {
      params: { limit: 100 },
    });
    return response.data.patterns.find((p) => p.name === code) ?? null;
  } catch (error) {
    console.error('Failed to find team pattern by code:', error);
    return null;
  }
}

/**
 * Find team judgment pattern by TTYPE (scoreConditions.TP value)
 * Returns the pattern with matching TP value, or null if not found
 */
export async function findPatternByTType(ttype: string): Promise<TeamJudgmentPattern | null> {
  try {
    const client = getApiClient();
    // Get all patterns and filter by TP value
    const response = await client.get<GetPatternsResponse>('/admin/team-judgment-patterns', { 
      params: { limit: 100 } 
    });
    
    const pattern = response.data.patterns.find(p => {
      const tp = p.scoreConditions?.TP;
      return tp === ttype;
    });
    
    return pattern || null;
  } catch (error) {
    console.error('Failed to find pattern by TTYPE:', error);
    return null;
  }
}
